import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Tab, Nav, Button, Alert } from 'react-bootstrap';
import { RoleUtils, HasRoles } from '@galaxy/lorule-react-commons';
import { ResponseError } from '@nintendo/forms';
import { useMsal } from '@azure/msal-react';
import Collapse, { Panel } from 'rc-collapse';
import 'rc-collapse/assets/index.css';
import moment from 'moment-timezone';
import ConfigContext from '../../contexts/ConfigContext';
import useAsyncWithState from '../../hooks/useAsyncWithState';
import { getActionMemo } from '../../api/actionMemoApi';
import Actuals from './tabs/Actuals';
import ApproversViewers from './tabs/ApproversViewers';
import Attachments from './tabs/Attachments';
import Comments from './tabs/Comments';
import Commitments from './tabs/Commitments';
import HistoryTab from './tabs/HistoryTab';
import Overview from './tabs/Overview';
import PendingCommitments from './tabs/PendingCommitments';
import { ActionMemo } from '../../api/actionMemoApiTypes';
import Spinner from '../common/Spinner';
import {
    AmStatusType,
    AM_LIST_PAGE_NAME,
    AM_LIST_PATH_NAME,
    PROJECT_UNDER_CONSTRUCTION_ERROR,
    CONTRACT_SIGNER_ORIGIN,
    REMINDER_DAYS,
} from '../../utils/Constants';
import AmDetailFooter from './AmDetailFooter';
import { useStateWithSessionStorage } from '../../hooks/useStateWithSessionStorage';
import { getStatusBadge, getDueInBadgeStyle } from '../../utils/displayUtils';
import AmUpdateModal from './modal/AmUpdateModal';
import SyncProjectModal from '../common/modal/SyncProjectModal';
import EmployeeIdContext from '../../contexts/EmployeeIdContext';
import MyApprovalContext from '../../contexts/MyApprovalContext';
import FavoriteButton from '../common/FavoriteButton';
import { getProjectBalance } from '../../api/projectBalanceApi';
import { ProjectBalance } from '../../api/projectBalanceApiTypes';
import AmProcessModal, { AmProcessFormType } from './modal/AmProcessModal';
import AmCreateModal from '../common/modal/AmCreateModal';
import { AmCreateFormType } from '../common/modal/amCreateformTypes';
import ReplaceProjectManagerModal from './modal/ReplaceProjectManagerModal';

interface RouteParams {
    id: string;
}

interface RouteLocationState {
    returnPageName?: string;
    returnPathName?: string;
}

const getDueInBadge = (dueDate: string | null | undefined) => {
    if (!dueDate) {
        return null;
    }
    const dueDateMoment = moment(dueDate);
    const startOfDay = moment().startOf('day');
    const dueIn = dueDateMoment.diff(startOfDay, 'days');
    let displayString = '';
    if (dueIn < 0) {
        displayString = 'Overdue';
    } else if (dueIn < 1) {
        displayString = 'Due Today';
    } else {
        displayString = `Due in ${dueIn} day${dueIn > 1 ? 's' : ''}`;
    }
    return <span className={`badge ${getDueInBadgeStyle(dueIn)}`}>{displayString}</span>;
};

const AmDetail = (routerProps: RouteComponentProps<RouteParams>) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const employeeId = useContext(EmployeeIdContext);
    const { id } = routerProps.match.params;
    const roles = RoleUtils.getRoles(instance);

    const [refresh, setRefresh] = useState(0);
    const [activeTab, setActiveTab] = useStateWithSessionStorage(`actionMemoTab${id}`, 'overview');
    const [responseError, setResponseError] = useState('');
    const [projectErrors, setProjectErrors] = useState([] as string[]);

    const {
        value: detail,
        loading,
        error: amError,
    } = useAsyncWithState(
        () => getActionMemo(config, Number(id), !RoleUtils.checkRole(roles, ['ActionMemo.Admin'])),
        {} as ActionMemo,
        [refresh],
    );

    const refreshMyApprovalCount = useContext(MyApprovalContext).refreshCount;

    useEffect(() => {
        if (detail && detail.errors) {
            setProjectErrors(detail.errors);
        }
    }, [detail, refresh]);

    // Ref callback to activate edit modal
    const setEditRef = useCallback(
        (node: HTMLButtonElement) => {
            if (node && detail && (!detail.actionRequested || !detail.justification)) {
                node.click();
            }
        },
        [detail],
    );

    const refreshDetail = () => {
        setRefresh(refresh + 1);
        refreshMyApprovalCount();
    };

    const approvedAm =
        detail.supNumber > 0 ||
        [
            AmStatusType.Approved.toString(),
            AmStatusType.Closed.toString(),
            AmStatusType.Voided.toString(),
        ].includes(detail.amStatus) ||
        [
            AmStatusType.Approved.toString(),
            AmStatusType.Closed.toString(),
            AmStatusType.Voided.toString(),
        ].includes(detail.priorStatus);

    if (amError) {
        return <ResponseError errorMessage={amError} />;
    }

    const { value: balance, error: balanceError } = useAsyncWithState(
        () =>
            detail &&
            approvedAm &&
            getProjectBalance(config, detail.id, !RoleUtils.checkRole(roles, ['ActionMemo.Admin'])),
        null as ProjectBalance | null,
        [detail],
    );

    const contractSigner =
        detail.approvers &&
        detail.approvers.find((approver) => approver.origin === CONTRACT_SIGNER_ORIGIN);

    const AttachmentCount =
        detail.attachments && detail.attachments.length > 0 ? (
            <span className="ml-1 badge badge-primary">{detail.attachments.length}</span>
        ) : null;
    const CommentsCount =
        detail.comments && detail.comments.length > 0 ? (
            <span className="ml-1 badge badge-primary">{detail.comments.length}</span>
        ) : null;

    const canEditDetails = !!employeeId && detail.currentUserIsCreatorOrProjectManager;

    const renderPreviousPageLink = () => {
        const locationState = routerProps.location.state as RouteLocationState;
        const returnPageName =
            locationState && locationState.returnPageName
                ? locationState.returnPageName
                : AM_LIST_PAGE_NAME;
        const returnPathName =
            locationState && locationState.returnPathName
                ? locationState.returnPathName
                : AM_LIST_PATH_NAME;

        return <Link to={returnPathName}>{returnPageName}</Link>;
    };

    const favoriteButton = (watching: boolean, onClick: () => void) => (
        <Button variant="link" size="lg" className="btn-link" onClick={onClick}>
            {watching ? <i className="fas fa-heart text-danger" /> : <i className="fal fa-heart" />}
        </Button>
    );

    return loading ? (
        <Spinner />
    ) : (
        <>
            <div className="container-fluid mt-3">
                <ResponseError errorMessage={responseError} />

                <div className="row row-page-header">
                    <div className="col-12 d-flex mb-2">
                        <div className="col-sm-6 mr-auto">
                            <h6>
                                {renderPreviousPageLink()} <i className="fas fa-chevron-right" />{' '}
                                {detail.amNumber}-{detail.supNumber}
                            </h6>
                            <div className="d-flex mb-1">
                                <h3 className="mr-2 text-weight-normal">{detail.subject}</h3>
                                {detail.confidential && (
                                    <div className="mr-2">
                                        <span className="text-dark badge badge-warning">
                                            CONFIDENTIAL
                                        </span>
                                    </div>
                                )}
                                <div className="mr-2">{getStatusBadge(detail.amStatus)}</div>
                                <div>{detail.dueDate && getDueInBadge(detail.dueDate)}</div>
                            </div>
                            <h6>
                                {detail.templateCode} {detail.templateDescription}
                            </h6>
                        </div>
                        <div className="mr-2 pr-3">
                            <FavoriteButton
                                initialFav={detail.watching}
                                id={detail.id}
                                amNumber={detail.amNumber}
                                supNumber={detail.supNumber}
                                setResponseError={setResponseError}
                                button={favoriteButton}
                            />
                        </div>
                    </div>
                </div>
                {detail.currentApprover &&
                    detail.currentApproverId &&
                    employeeId &&
                    detail.currentApproverId !== employeeId && (
                        <Alert variant="info">
                            You are acting on behalf of <b>{detail.currentApproverName}</b>.
                        </Alert>
                    )}
                <Collapse
                    accordion
                    activeKey={activeTab}
                    defaultActiveKey="overview"
                    onChange={(eventKey: any) => setActiveTab(eventKey)}
                    className="desktop-hide"
                >
                    <Panel header="Overview" headerClass="my-header-class" key="overview">
                        <Overview
                            details={detail}
                            balance={balance}
                            contractSigner={contractSigner}
                            approvedAm={approvedAm}
                        />
                    </Panel>
                    <Panel
                        header={
                            <div>
                                Attachments
                                {AttachmentCount}
                            </div>
                        }
                        key="attachments"
                    >
                        <Attachments detail={detail} refresh={refreshDetail} />
                    </Panel>
                    <Panel
                        header={`${!detail.template.autoApprove ? 'Approvers & ' : ''}Viewers`}
                        key="approvers"
                    >
                        <ApproversViewers amDetail={detail} refresh={refreshDetail} />
                    </Panel>
                    <Panel
                        header={
                            <div>
                                Comments
                                {CommentsCount}
                            </div>
                        }
                        key="comments"
                    >
                        <Comments detail={detail} refresh={refreshDetail} />
                    </Panel>
                    <Panel header="History" key="history">
                        <HistoryTab events={detail.events} />
                    </Panel>
                </Collapse>
                <div className="mobile-hide">
                    {balanceError === PROJECT_UNDER_CONSTRUCTION_ERROR ? (
                        <Alert variant="info">Oracle Project is being created.</Alert>
                    ) : (
                        <ResponseError errorMessage={balanceError} />
                    )}
                    <Alert show={projectErrors.length > 0} variant="danger">
                        <Alert.Heading>
                            The following project interface error
                            {projectErrors.length > 1 ? 's' : ''} have occurred:
                        </Alert.Heading>
                        {projectErrors.length > 1 ? (
                            <ul>
                                {projectErrors.map((error) => (
                                    <li>{error}</li>
                                ))}
                            </ul>
                        ) : (
                            projectErrors[0]
                        )}

                        {(canEditDetails || RoleUtils.checkRole(roles, ['ActionMemo.Admin'])) && (
                            <>
                                <hr />
                                <div className="d-flex justify-content-end">
                                    <SyncProjectModal
                                        amId={detail.id}
                                        toggleButton={
                                            <Button variant="outline-danger">
                                                <i className="fas fa-sync-alt" /> Sync Project
                                            </Button>
                                        }
                                        onSubmit={refreshDetail}
                                    />
                                </div>
                            </>
                        )}
                    </Alert>
                    {canEditDetails &&
                        detail.amStatus === AmStatusType.Draft &&
                        (!detail.actionRequested || !detail.justification) && (
                            <Alert variant="warning">
                                Please{' '}
                                <AmUpdateModal
                                    title="Edit Action Memo"
                                    toggleButton={
                                        <Button variant="link" className="align-baseline px-0">
                                            edit this action memo
                                        </Button>
                                    }
                                    id={detail.id}
                                    amNumber={detail.amNumber}
                                    supNumber={detail.supNumber}
                                    confidential={detail.confidential}
                                    subject={detail.subject}
                                    actionRequested={detail.actionRequested}
                                    justification={detail.justification}
                                    budgetAmount={detail.budgetAmount}
                                    internalCostAmount={detail.internalCostAmount}
                                    revenueAmount={detail.revenueAmount}
                                    creationDate={detail.projectCreationDate}
                                    completionDate={detail.completionDate}
                                    nclDepartmentCode={detail.nclDepartmentCode}
                                    nclDepartmentDescription={detail.nclDepartmentDescription}
                                    nclDevCode={detail.nclDevCode}
                                    nclDevCodeDescription={detail.nclDevCodeDescription}
                                    newCompletionDate={
                                        detail.supNumber <= 0
                                            ? detail.completionDate
                                            : detail.newCompletionDate
                                    }
                                    template={detail.template}
                                    onSubmit={refreshDetail}
                                />{' '}
                                and provide the action requested and justification.
                            </Alert>
                        )}
                    {(canEditDetails || detail.currentUserIsProjectAdministrator) &&
                        detail.supNumber <= 0 &&
                        detail.amStatus === AmStatusType.Inactive &&
                        (detail.priorStatus === AmStatusType.Draft ||
                            detail.priorStatus === AmStatusType.Pending ||
                            detail.priorStatus === AmStatusType.Hold ||
                            detail.priorStatus === AmStatusType.Approved) && (
                            <Alert variant="warning">
                                Please{' '}
                                <AmProcessModal
                                    title="Reactivate Action Memo"
                                    toggleButton={
                                        <Button variant="link" className="align-baseline px-0">
                                            reactivate this Action Memo
                                        </Button>
                                    }
                                    formType={AmProcessFormType.REACTIVATE}
                                    amId={detail.id}
                                    amNumber={detail.amNumber}
                                    supNumber={detail.supNumber}
                                    creationDate={detail.projectCreationDate}
                                    completionDate={detail.completionDate}
                                    templateMaximumCompletionDays={detail.template.completionDays}
                                    onSubmit={refreshDetail}
                                />{' '}
                                if you need to update or spend the budget.
                            </Alert>
                        )}
                    {(canEditDetails || detail.currentUserIsProjectAdministrator) &&
                        detail.supNumber <= 0 &&
                        detail.amStatus === AmStatusType.Approved &&
                        moment().isSameOrAfter(
                            moment(detail.completionDate).subtract(REMINDER_DAYS, 'days'),
                        ) && (
                            <Alert variant="warning">
                                The completion date for this action memo is within 2 weeks. If
                                necessary, please{' '}
                                <AmProcessModal
                                    title="Change Completion Date"
                                    toggleButton={
                                        <Button variant="link" className="align-baseline px-0">
                                            change the completion date
                                        </Button>
                                    }
                                    formType={AmProcessFormType.CHANGE_COMPLETION_DATE}
                                    amId={detail.id}
                                    amNumber={detail.amNumber}
                                    supNumber={detail.supNumber}
                                    creationDate={detail.projectCreationDate}
                                    completionDate={detail.completionDate}
                                    newCompletionDate={detail.newCompletionDate}
                                    templateMaximumCompletionDays={detail.template.completionDays}
                                    onSubmit={refreshDetail}
                                />{' '}
                                or{' '}
                                <AmCreateModal
                                    title="Add Supplement"
                                    toggleButton={
                                        <Button variant="link" className="align-baseline px-0">
                                            add a supplemental action memo
                                        </Button>
                                    }
                                    formType={AmCreateFormType.SUPPLEMENT}
                                    amId={detail.id}
                                    subject={detail.subject}
                                    projectManagerId={detail.projectManagerId}
                                    projectManagerName={detail.projectManagerName}
                                    onSubmit={refreshDetail}
                                    routerProps={routerProps}
                                />{' '}
                                to extend the completion date.
                            </Alert>
                        )}
                    {detail.supNumber <= 0 &&
                        detail.amStatus === AmStatusType.Approved &&
                        !detail.projectManagerValid && (
                            <div>
                                {canEditDetails || detail.currentUserIsProjectAdministrator ? (
                                    <Alert variant="warning">
                                        The project manager, <b>{detail.projectManagerName}</b>, is
                                        no longer an active user. If you need to supplement this
                                        action memo, please{' '}
                                        <ReplaceProjectManagerModal
                                            title="Replace Project Manager"
                                            toggleButton={
                                                <Button
                                                    variant="link"
                                                    className="align-baseline px-0"
                                                >
                                                    replace the project manager
                                                </Button>
                                            }
                                            amId={detail.id}
                                            amNumber={detail.amNumber}
                                            personId={detail.projectManagerId}
                                            onSubmit={refreshDetail}
                                        />{' '}
                                        to an active user.
                                    </Alert>
                                ) : (
                                    <Alert variant="warning">
                                        The project manager, <b>{detail.projectManagerName}</b>, is
                                        no longer an active user. If you need to supplement this
                                        action memo, a project administrator can replace the project
                                        manager to an active user.
                                    </Alert>
                                )}
                            </div>
                        )}
                    <HasRoles roles={['ActionMemo.Admin']}>
                        <div>
                            {detail.statusChangeRequest && (
                                <Alert variant="warning">
                                    <b>{detail.statusChangeRequest.requestedByName}</b> has
                                    requested the status to change from{' '}
                                    <b>{detail.statusChangeRequest.currentStatus}</b> to{' '}
                                    <b>{detail.statusChangeRequest.requestedStatus}</b>.{' '}
                                    {detail.statusChangeRequest.requestedStatus ===
                                        AmStatusType.Closed &&
                                        (detail.amStatus === AmStatusType.Approved ||
                                            (detail.amStatus === AmStatusType.Inactive &&
                                                (detail.priorStatus === AmStatusType.Draft ||
                                                    detail.priorStatus === AmStatusType.Pending ||
                                                    detail.priorStatus === AmStatusType.Hold ||
                                                    detail.priorStatus ===
                                                        AmStatusType.Approved))) && (
                                            <>
                                                Please review the request and{' '}
                                                <AmProcessModal
                                                    title="Close Action Memo"
                                                    toggleButton={
                                                        <Button
                                                            variant="link"
                                                            className="align-baseline px-0"
                                                        >
                                                            close the action memo
                                                        </Button>
                                                    }
                                                    formType={AmProcessFormType.CLOSE}
                                                    amId={detail.id}
                                                    amNumber={detail.amNumber}
                                                    supNumber={detail.supNumber}
                                                    onSubmit={refreshDetail}
                                                />{' '}
                                                or{' '}
                                                <AmProcessModal
                                                    title="Cancel Status Change Request"
                                                    toggleButton={
                                                        <Button
                                                            variant="link"
                                                            className="align-baseline px-0"
                                                        >
                                                            cancel the status change request
                                                        </Button>
                                                    }
                                                    formType={AmProcessFormType.CANCEL_REQUEST}
                                                    amId={detail.id}
                                                    amNumber={detail.amNumber}
                                                    supNumber={detail.supNumber}
                                                    onSubmit={refreshDetail}
                                                />
                                                .
                                            </>
                                        )}
                                </Alert>
                            )}
                        </div>
                    </HasRoles>
                    <Tab.Container
                        activeKey={activeTab}
                        onSelect={(eventKey: any) => setActiveTab(eventKey)}
                        defaultActiveKey="overview"
                    >
                        <Nav className="nav-nin mt-2 mx-2">
                            <Nav.Item>
                                <Nav.Link eventKey="overview">Overview</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="attachments">
                                    Attachments
                                    {AttachmentCount}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="approvers">
                                    {!detail.template.autoApprove && 'Approvers & '}Viewers
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="comments">
                                    Comments
                                    {CommentsCount}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="history">History</Nav.Link>
                            </Nav.Item>
                            {!balanceError && approvedAm && (
                                <>
                                    <Nav.Item>
                                        <Nav.Link eventKey="pendingcommitments">
                                            Pending Commitments
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="commitments">Commitments</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="actuals">Actuals</Nav.Link>
                                    </Nav.Item>
                                </>
                            )}
                        </Nav>
                        <Tab.Content className="tab-nin mx-2">
                            <Tab.Pane eventKey="overview">
                                <Overview
                                    details={detail}
                                    balance={balance}
                                    contractSigner={contractSigner}
                                    approvedAm={approvedAm}
                                />
                            </Tab.Pane>
                            <Tab.Pane eventKey="attachments">
                                <Attachments detail={detail} refresh={refreshDetail} />
                            </Tab.Pane>
                            <Tab.Pane eventKey="approvers" mountOnEnter>
                                <ApproversViewers amDetail={detail} refresh={refreshDetail} />
                            </Tab.Pane>
                            <Tab.Pane eventKey="comments" mountOnEnter>
                                <Comments detail={detail} refresh={refreshDetail} />
                            </Tab.Pane>
                            <Tab.Pane eventKey="history" mountOnEnter>
                                <HistoryTab events={detail.events} />
                            </Tab.Pane>
                            {approvedAm && (
                                <>
                                    <Tab.Pane eventKey="pendingcommitments" mountOnEnter>
                                        <PendingCommitments
                                            amId={detail.id}
                                            amNumber={detail.amNumber}
                                            total={balance && balance.fundsCheckTotal}
                                        />
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="commitments" mountOnEnter>
                                        <Commitments
                                            amId={detail.id}
                                            amNumber={detail.amNumber}
                                            total={balance && balance.commitmentTotal}
                                        />
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="actuals" mountOnEnter>
                                        <Actuals
                                            amId={detail.id}
                                            amNumber={detail.amNumber}
                                            total={balance && balance.actualTotal}
                                        />
                                    </Tab.Pane>
                                </>
                            )}
                        </Tab.Content>
                    </Tab.Container>
                </div>
            </div>
            <AmDetailFooter
                detail={detail}
                contractSigner={contractSigner}
                refresh={refreshDetail}
                refreshApprovalCount={refreshMyApprovalCount}
                editRef={setEditRef}
                routerProps={routerProps}
                canEditDetails={canEditDetails}
            />
        </>
    );
};

export default AmDetail;
